import { RectNode, RectNodeModel, h } from '@logicflow/core';
import { renderMarkers } from './markers';
import { getThemeStyle } from '../theme';
import { renderWrappedText } from './textUtils';

/** Collapsed dimensions */
const COLLAPSED_WIDTH = 100;
const COLLAPSED_HEIGHT = 80;

class TransactionView extends RectNode {
  getShape() {
    const { model } = this.props;
    const { x, y, width, height, radius } = model;
    const style = model.getNodeStyle();
    const props = model.properties || {};
    const isExpanded = props.isExpanded !== false;

    const outerX = x - width / 2;
    const outerY = y - height / 2;
    const inset = 3;
    const markerBoxY = y + height / 2 - 16;

    const children = [
      // Outer border
      h('rect', {
        x: outerX,
        y: outerY,
        width,
        height,
        rx: radius,
        ry: radius,
        ...style,
      }),
      // Inner border (double-border BPMN Transaction marker)
      h('rect', {
        x: outerX + inset,
        y: outerY + inset,
        width: width - inset * 2,
        height: height - inset * 2,
        rx: radius - 1,
        ry: radius - 1,
        fill: 'none',
        stroke: style.stroke,
        strokeWidth: 1.2,
      }),
      // BPMN [+]/[-] marker at bottom center
      h('rect', {
        x: x - 7,
        y: markerBoxY,
        width: 14,
        height: 14,
        rx: 1,
        ry: 1,
        fill: 'none',
        stroke: style.stroke,
        strokeWidth: 1,
      }),
      h('path', {
        d: isExpanded
          ? `M ${x} ${markerBoxY + 2} L ${x} ${markerBoxY + 12} M ${x - 5} ${markerBoxY + 7} L ${x + 5} ${markerBoxY + 7}`
          : `M ${x - 5} ${markerBoxY + 7} L ${x + 5} ${markerBoxY + 7}`,
        stroke: style.stroke,
        strokeWidth: 1.2,
        fill: 'none',
      }),
      // Invisible click target for toggle
      h('rect', {
        x: x - 10,
        y: markerBoxY - 3,
        width: 20,
        height: 20,
        fill: 'transparent',
        stroke: 'none',
        style: 'cursor: pointer;',
        onClick: () => this._toggleExpand(),
      }),
    ];


    // Wrapped text inside the shape
    const label = (props && props.name) || '';
    if (label) {
      children.push(renderWrappedText(h, label, x, y - 4, width - 20, { graphModel: model.graphModel }));
    }

    // Loop / Multi-Instance markers (rendered left of the [+] marker)
    children.push(...renderMarkers(x - 18, y, width, height, props, style.stroke));

    return h('g', {}, children);
  }

  _toggleExpand() {
    const { model } = this.props;
    const props = model.properties || {};
    const isExpanded = props.isExpanded !== false;

    if (isExpanded) {
      model.setProperties({
        isExpanded: false,
        _expandedWidth: model.width,
        _expandedHeight: model.height,
      });
      model.width = COLLAPSED_WIDTH;
      model.height = COLLAPSED_HEIGHT;
      model.setProperties({ width: COLLAPSED_WIDTH, height: COLLAPSED_HEIGHT });
    } else {
      const ew = props._expandedWidth || 300;
      const eh = props._expandedHeight || 200;
      model.setProperties({ isExpanded: true });
      model.width = ew;
      model.height = eh;
      model.setProperties({ width: ew, height: eh });
    }
  }
}

class TransactionModel extends RectNodeModel {
  initNodeData(data) {
    super.initNodeData(data);
    this.width = data.properties?.width || 140;
    this.height = data.properties?.height || 80;
    this.radius = 5;
    this.text = { ...this.text, editable: false, draggable: false, value: '' };
  }


  getNodeStyle() {
    const style = super.getNodeStyle();
    const t = getThemeStyle(this.graphModel, 'transaction');
    style.stroke = t.stroke || '#096dd9';
    style.strokeWidth = t.strokeWidth ?? 2;
    style.fill = t.fill || '#e6f7ff';
    return style;
  }
}

export const Transaction = {
  type: 'epx:Transaction',
  view: TransactionView,
  model: TransactionModel,
};
